import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { BLOG_POSTS } from "../lib/blog";

const SITE = "https://riznwash.com";
const EASE = [0.22, 1, 0.36, 1];

export default function BlogIndex() {
  const path = "/blog";
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Blog",
      "@id": `${SITE}${path}#blog`,
      name: "The RIZ N WASH Journal",
      description:
        "Notes, comparisons and honest guides from the team behind RIZ N WASH — Gurugram's premium doorstep laundry & dry cleaning brand.",
      url: `${SITE}${path}`,
      inLanguage: "en-IN",
      publisher: { "@id": `${SITE}/#organization` },
      blogPost: BLOG_POSTS.map((p) => ({
        "@type": "BlogPosting",
        headline: p.title,
        description: p.description,
        datePublished: p.published,
        author: { "@type": "Organization", name: "RIZ N WASH" },
        url: `${SITE}/blog/${p.slug}`,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "RIZ N WASH", item: SITE },
        { "@type": "ListItem", position: 2, name: "Journal", item: `${SITE}${path}` },
      ],
    },
  ];

  const [featured, ...rest] = BLOG_POSTS;

  return (
    <div className="min-h-screen bg-rw-bg text-rw-ink" data-testid="blog-index-page">
      <Seo
        title="The RIZ N WASH Journal"
        description="Notes, comparisons and honest guides from the team behind RIZ N WASH — Gurugram's premium doorstep laundry & dry cleaning brand."
        path={path}
        jsonLd={jsonLd}
      />
      <Nav />

      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <div className="overline mb-4">Journal · Long reads &amp; guides</div>
          <h1
            className="font-display font-light tracking-tight text-rw-ink leading-[1.02] text-4xl sm:text-5xl lg:text-6xl"
            data-testid="blog-index-heading"
          >
            Notes from the studio.
          </h1>
          <p className="mt-6 md:mt-8 text-lg md:text-xl text-rw-ink2 max-w-3xl leading-relaxed">
            Comparisons, price benchmarks and the occasional founder rant — from the team that folds your laundry.
          </p>
        </motion.div>
      </section>

      {/* Featured post */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-12 md:pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <Link
            to={`/blog/${featured.slug}`}
            className="group grid md:grid-cols-12 gap-6 md:gap-10 items-center rounded-2xl overflow-hidden"
            data-testid={`blog-featured-${featured.slug}`}
          >
            <div className="md:col-span-6 overflow-hidden rounded-2xl aspect-[16/10]">
              <img
                src={featured.hero}
                alt={featured.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="md:col-span-6">
              <div className="overline text-rw-blue mb-3">
                Featured · {featured.category}
              </div>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-rw-ink group-hover:text-rw-blue transition-colors">
                {featured.title}
              </h2>
              <p className="mt-4 text-rw-ink2 text-base md:text-lg leading-relaxed">
                {featured.description}
              </p>
              <div className="mt-6 text-sm text-rw-ink3">
                {featured.read} ·{" "}
                {new Date(featured.published).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </div>
            </div>
          </Link>
        </motion.div>
      </section>

      {/* Grid of remaining posts */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-24">
        <div className="border-t border-rw-ink/10 pt-10 mb-8">
          <div className="overline">Latest</div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-list">
          {rest.map((p, i) => (
            <motion.div
              key={p.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: EASE, delay: i * 0.05 }}
            >
              <Link
                to={`/blog/${p.slug}`}
                className="group block rounded-2xl overflow-hidden border border-rw-ink/10 hover:border-rw-blue transition-all"
                data-testid={`blog-card-${p.slug}`}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={p.hero}
                    alt={p.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <div className="overline text-rw-ink3 mb-2">
                    {p.category} · {p.read}
                  </div>
                  <h3 className="font-display text-xl md:text-2xl leading-tight text-rw-ink group-hover:text-rw-blue transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-rw-ink2 text-sm leading-relaxed line-clamp-3">
                    {p.description}
                  </p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
